import React, { useState, useEffect } from 'react';
import { Modal } from '../../../components/common/Modal';
import { Button } from '../../../components/common/Button';
import { contactsAPI } from '../../../services/api';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const UNITS = ['pcs','kg','litre','meter','sqft','sqm','bag','ton','box','set','day','hour','ls','other'];
const EMPTY_LINE = { description:'', quantity:1, unit:'pcs', unitPrice:0, taxRate:0, taxAmount:0, total:0 };

function buildEmpty() {
  return {
    title:'', invoiceNo:'', vendorName:'', vendor:'',
    invoiceDate: format(new Date(),'yyyy-MM-dd'), dueDate:'',
    status:'pending', paymentMode:'', paidAmount:0, discount:0, notes:'',
    lineItems:[{ ...EMPTY_LINE }],
  };
}

const inp = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white transition-all";
const sel = "w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white transition-all appearance-none";

export function CostEntryForm({ isOpen, onClose, onSave, editing, costType }) {
  const [contacts, setContacts] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState(buildEmpty());

  useEffect(() => {
    if (!isOpen) return;
    const type = costType === 'material' ? 'vendor' : 'contractor';
    contactsAPI.getAll({ type, limit:200 }).then(r => setContacts(r.data||[])).catch(()=>{});
  }, [isOpen, costType]);

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setForm({
        title:       editing.title||'',
        invoiceNo:   editing.invoiceNo||'',
        vendorName:  editing.vendorName||'',
        vendor:      editing.vendor?._id||'',
        invoiceDate: editing.invoiceDate ? format(new Date(editing.invoiceDate),'yyyy-MM-dd') : format(new Date(),'yyyy-MM-dd'),
        dueDate:     editing.dueDate ? format(new Date(editing.dueDate),'yyyy-MM-dd') : '',
        status:      editing.status||'pending',
        paymentMode: editing.paymentMode||'',
        paidAmount:  editing.paidAmount||0,
        discount:    editing.discount||0,
        notes:       editing.notes||'',
        lineItems:   editing.lineItems?.length ? editing.lineItems : [{ ...EMPTY_LINE }],
      });
    } else {
      setForm(buildEmpty());
    }
  }, [editing, isOpen]);

  const calcLine = l => {
    const sub = Number(l.quantity) * Number(l.unitPrice);
    const tax = sub * (Number(l.taxRate) / 100);
    return { ...l, taxAmount: +tax.toFixed(2), total: +(sub + tax).toFixed(2) };
  };
  const updLine = (i,k,v) => setForm(f => { const ls=[...f.lineItems]; ls[i]=calcLine({...ls[i],[k]:v}); return{...f,lineItems:ls}; });
  const addLine  = () => setForm(f => ({ ...f, lineItems:[...f.lineItems,{...EMPTY_LINE}] }));
  const delLine  = i  => setForm(f => ({ ...f, lineItems:f.lineItems.filter((_,j)=>j!==i) }));
  const s = (k,v) => setForm(f => ({...f,[k]:v}));

  const sub   = form.lineItems.reduce((a,l)=>a+Number(l.quantity)*Number(l.unitPrice),0);
  const tax   = form.lineItems.reduce((a,l)=>a+Number(l.taxAmount),0);
  const total = sub + tax - Number(form.discount);

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await onSave({
        ...form,
        costType,
        vendor: form.vendor || null,  // prevent empty string BSONError
      });
    } catch(err) {
      // error handled by parent
    } finally { setSaving(false); }
  };

  const typeLabel = costType === 'material' ? 'Material' : 'Sub-Contractor';

  return (
    <Modal isOpen={isOpen} onClose={onClose}
      title={(editing?'Edit ':'Add ') + typeLabel + ' Invoice'}
      size="xl">
      <form onSubmit={submit}>
        {/* ── Section 1: Invoice Header ── */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Title / Description *</label>
            <input value={form.title} onChange={e=>s('title',e.target.value)} required
              placeholder={costType==='material'?'Steel rods — Site A':'Electrical work — Floor 2'}
              className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Invoice No.</label>
            <input value={form.invoiceNo} onChange={e=>s('invoiceNo',e.target.value)}
              placeholder="Auto-generated if left blank" className={inp + " font-mono"} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Invoice Date</label>
            <input type="date" value={form.invoiceDate} onChange={e=>s('invoiceDate',e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Due Date</label>
            <input type="date" value={form.dueDate} onChange={e=>s('dueDate',e.target.value)} className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
            <select value={form.status} onChange={e=>s('status',e.target.value)} className={sel}>
              {['draft','pending','approved','paid','cancelled'].map(v=>
                <option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>
              )}
            </select>
          </div>
        </div>

        {/* ── Section 2: Party ── */}
        <div className="grid grid-cols-2 gap-4 mb-5 p-4 bg-gray-50 rounded-2xl border border-gray-100">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              {costType==='material'?'Vendor / Supplier':'Sub-Contractor'} (from Contacts)
            </label>
            <select value={form.vendor} onChange={e=>s('vendor',e.target.value)} className={sel}>
              <option value="">Select from contacts...</option>
              {contacts.map(c=>(
                <option key={c._id} value={c._id}>
                  {c.name}{c.company?' — '+c.company:''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Or Enter Name Manually</label>
            <input value={form.vendorName} onChange={e=>s('vendorName',e.target.value)}
              placeholder="If not in contacts list" className={inp} />
          </div>
        </div>

        {/* ── Section 3: Line Items ── */}
        <div className="mb-5">
          <div className="flex justify-between items-center mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {costType==='material'?'Materials / Items':'Work Items / Scope of Work'}
            </label>
            <button type="button" onClick={addLine}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors">
              <PlusIcon className="w-3.5 h-3.5" /> Add Line
            </button>
          </div>

          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid bg-gray-800 text-white text-xs font-semibold px-3 py-2.5"
              style={{gridTemplateColumns:'1fr 60px 80px 100px 70px 100px 32px'}}>
              <span>Description</span>
              <span className="text-right">Qty</span>
              <span className="pl-1">Unit</span>
              <span className="text-right">Rate (₹)</span>
              <span className="text-right">Tax %</span>
              <span className="text-right">Total (₹)</span>
              <span />
            </div>

            {/* Rows */}
            {form.lineItems.map((l,i)=>(
              <div key={i}
                className={'grid items-center gap-0 border-t border-gray-100 px-3 py-2 transition-colors ' + (i%2===0?'bg-white':'bg-gray-50/50')}
                style={{gridTemplateColumns:'1fr 60px 80px 100px 70px 100px 32px'}}>
                <input value={l.description} onChange={e=>updLine(i,'description',e.target.value)}
                  className="bg-transparent border-0 focus:outline-none text-sm text-gray-800 pr-2 w-full"
                  placeholder="Item description..." />
                <input type="number" value={l.quantity} onChange={e=>updLine(i,'quantity',e.target.value)}
                  className="bg-transparent border-0 focus:outline-none text-sm text-right text-gray-800 w-full" min="0" step="0.01" />
                <select value={l.unit} onChange={e=>updLine(i,'unit',e.target.value)}
                  className="bg-transparent border-0 focus:outline-none text-xs text-gray-600 pl-1 w-full">
                  {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
                </select>
                <input type="number" value={l.unitPrice} onChange={e=>updLine(i,'unitPrice',e.target.value)}
                  className="bg-transparent border-0 focus:outline-none text-sm text-right text-gray-800 w-full" min="0" step="0.01" />
                <input type="number" value={l.taxRate} onChange={e=>updLine(i,'taxRate',e.target.value)}
                  className="bg-transparent border-0 focus:outline-none text-sm text-right text-gray-800 w-full" min="0" max="100" step="0.1" />
                <div className="text-right font-bold text-sm text-gray-900">{(l.total||0).toFixed(2)}</div>
                <div className="flex justify-center">
                  {form.lineItems.length > 1 &&
                    <button type="button" onClick={()=>delLine(i)} className="text-gray-300 hover:text-red-500 transition-colors p-0.5">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  }
                </div>
              </div>
            ))}

            {/* Totals */}
            <div className="border-t-2 border-gray-200 bg-gray-50 px-4 py-4 flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span><span className="font-medium">₹{sub.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Tax</span><span className="font-medium">₹{tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>Discount (₹)</span>
                  <input type="number" value={form.discount} onChange={e=>s('discount',Number(e.target.value))}
                    className="w-28 text-right border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" min="0" />
                </div>
                <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300">
                  <span className="font-bold text-gray-800 text-base">Grand Total</span>
                  <span className="font-extrabold text-xl text-indigo-600">₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 4: Payment ── */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Payment Mode</label>
            <select value={form.paymentMode} onChange={e=>s('paymentMode',e.target.value)} className={sel}>
              <option value="">Select mode...</option>
              {[['cash','Cash'],['bank_transfer','Bank Transfer'],['cheque','Cheque'],['upi','UPI'],['card','Card'],['other','Other']].map(([v,l])=>
                <option key={v} value={v}>{l}</option>
              )}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Amount Paid (₹)</label>
            <input type="number" value={form.paidAmount} onChange={e=>s('paidAmount',Number(e.target.value))}
              min="0" placeholder="0" className={inp} />
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Notes / Remarks</label>
          <textarea value={form.notes} onChange={e=>s('notes',e.target.value)} rows={2}
            placeholder="Terms, conditions, remarks..."
            className={inp + " resize-none"} />
        </div>

        {/* Balance indicator */}
        {form.paidAmount > 0 && (
          <div className={`rounded-xl px-4 py-3 mb-4 flex items-center justify-between text-sm font-semibold ${
            form.paidAmount >= total ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-amber-50 border border-amber-200 text-amber-700'
          }`}>
            <span>{form.paidAmount >= total ? '✅ Fully Paid' : '⚠️ Partially Paid'}</span>
            <span>Balance: ₹{Math.max(0, total - form.paidAmount).toFixed(2)}</span>
          </div>
        )}

        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <Button type="submit" loading={saving} className="flex-1 justify-center">
            {editing ? 'Update' : 'Add'} {typeLabel} Invoice
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}